import * as React from "react";

// One reading of the screen, for the components that must CHANGE SHAPE on a phone rather than
// merely restyle (a table that becomes cards, a popover that becomes a sheet). CSS breakpoints
// cannot do that: the DOM itself differs. Everything else stays in Tailwind's responsive prefixes.

/** The phone breakpoint, the same 640px Tailwind's `sm:` starts at, so markup and logic agree. */
export const PHONE_QUERY = "(max-width: 639.98px)";

export function useMediaQuery(query: string): boolean {
  const get = React.useCallback(
    () => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(query).matches : false),
    [query],
  );
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    [query],
  );
  return React.useSyncExternalStore(subscribe, get, () => false);
}

/** True below Tailwind's `sm:` — a phone held upright. */
export function useIsPhone(): boolean {
  return useMediaQuery(PHONE_QUERY);
}

/**
 * Whether this page is running as an installed app (home screen / desktop app window), not in a
 * browser tab. `display-mode: standalone` is the standard; iOS Safari predates it and reports
 * `navigator.standalone` instead, so both are asked.
 */
export function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    nav.standalone === true ||
    (typeof window.matchMedia === "function" &&
      (window.matchMedia("(display-mode: standalone)").matches || window.matchMedia("(display-mode: fullscreen)").matches))
  );
}

export function useStandalone(): boolean {
  const media = useMediaQuery("(display-mode: standalone)");
  return media || isStandaloneDisplay();
}
